import ActionTextIcon from "@/components/icons/ActionTextIcon";
import PopconfirmDeleteIcon from "@/components/icons/PopconfirmDeleteIcon";
import { createLinkHref, openUrl } from "@/utils/utils";
import { Card, Popconfirm, Tooltip } from "antd";
import { useTranslation } from "react-i18next";
import { useCompetenceCheck } from "../../../../app/contexts/CompetenceCheckContext";
import { useDeletePartnerConfigs } from "../../../../common/api/queries";
import { useAuthToken, useCurrentAccount } from "../../../auth/hooks";
import { PartnerSsoType } from "../../enums";
import { PartnerConfig } from "../../types";
import { getFinancialSectorForSsoType, resolvePartnerConfigBackgroundPictureUrl } from "../../utils";
import styles from "./PartnerConfigListView.module.scss";

interface Props {
  configs: PartnerConfig[];
  hasAdminPartnerConfigsPermission: boolean;
  logoProvider: () => string;
  onPartnerSsoGet: (ssoType: PartnerSsoType, username?: string) => void;
  onLogoutClick: (configs: PartnerConfig[]) => void;
  onUpdateClick: (configs: PartnerConfig[]) => void;
}

const CONFIG_CARD_HEIGHT = 210;

export const PartnerConfigsCardView = ({
  configs,
  hasAdminPartnerConfigsPermission,
  logoProvider,
  onPartnerSsoGet,
  onLogoutClick,
  onUpdateClick,
}: Props) => {
  const { t } = useTranslation();

  const token = useAuthToken();
  const userAccount = useCurrentAccount();

  const { guardCompetenceAction } = useCompetenceCheck();

  const deletePartnerConfigs = useDeletePartnerConfigs();

  const backgroundPictureConfig = configs.find((config) => config.backgroundPicture);

  const handlePartnerConfigClick = (config: PartnerConfig) => {
    switch (config.ssoType) {
      case PartnerSsoType.MONLY:
      case PartnerSsoType.METLIFE:
        onPartnerSsoGet(config.ssoType);
        break;
      default: {
        const partnerFinancialSector = config.ssoType ? getFinancialSectorForSsoType(config.ssoType) : undefined;

        if (partnerFinancialSector) {
          guardCompetenceAction({ sector: partnerFinancialSector }, () =>
            openUrl(createLinkHref(config.link), "_blank"),
          );
          break;
        }

        openUrl(createLinkHref(config.link), "_blank");
        break;
      }
    }
  };

  return (
    <Card
      title={configs[0] ? configs[0].institution?.name || configs[0].label : ""}
      hoverable
      size="small"
      style={{ marginBottom: 20, cursor: "default" }}
      extra={
        <>
          {configs.find((config) => config.ssoType) && (
            <Popconfirm
              title={t("partnerSso.helpers.logoutConfirm")}
              icon={<PopconfirmDeleteIcon />}
              cancelText={t("common.no")}
              okText={t("common.yes")}
              okType="danger"
              onConfirm={() => onLogoutClick(configs)}
            >
              <Tooltip title={t("common.logout")} placement="bottom">
                <span>
                  <ActionTextIcon icon="logout" color="orange" />
                </span>
              </Tooltip>
            </Popconfirm>
          )}
          {hasAdminPartnerConfigsPermission && (
            <>
              <Tooltip title={t("common.edit")} placement="bottom">
                <span>
                  <ActionTextIcon icon="edit" color="blue" onClick={() => onUpdateClick(configs)} />
                </span>
              </Tooltip>

              <Popconfirm
                title={t("partnerConfig.helpers.deleteConfirm")}
                icon={<PopconfirmDeleteIcon />}
                cancelText={t("common.no")}
                okText={t("common.yes")}
                okType="danger"
                onConfirm={() => deletePartnerConfigs.mutate(configs.map((config) => config.id))}
              >
                <Tooltip title={t("common.delete")} placement="bottom">
                  <span>
                    <ActionTextIcon icon="delete" color="red" />
                  </span>
                </Tooltip>
              </Popconfirm>
            </>
          )}
        </>
      }
    >
      <div className={styles.partnerWrapper}>
        <div className={styles.partnerLogoContainer}>
          <img
            className={styles.partnerLogoImg}
            src={
              backgroundPictureConfig?.backgroundPicture
                ? resolvePartnerConfigBackgroundPictureUrl(
                    backgroundPictureConfig.id,
                    backgroundPictureConfig.backgroundPicture.id,
                    token,
                    userAccount?.id,
                  )
                : logoProvider()
            }
            alt="partner-logo"
          />
        </div>
        <div className={styles.partnerLinksContainer}>
          {configs.map((config) => (
            <div
              className={styles.partnerLink}
              key={config.id}
              style={{ height: CONFIG_CARD_HEIGHT / configs.length }}
              onClick={() => handlePartnerConfigClick(config)}
            >
              <span>{config.label}</span>
            </div>
          ))}
        </div>
      </div>
    </Card>
  );
};
